/**
 * StudyArtifacts — Right panel of the conversational study design UI.
 *
 * Renders all result visualizations once a synthetic control job completes:
 *   - Stat tiles (Lift %, iROAS, Incremental Sales, Confidence)
 *   - Lift chart  (treatment vs synthetic control time-series)
 *   - Power curve (effect size vs statistical power)
 *   - Market results table
 *   - AI Insights card (summary + recommendations from SyntheticControlAgent)
 *
 * Shows a waiting state before the job completes.
 */

import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import axiosInstance from "../../../../utils/axiosInstance";
import {
  GEO_LIFT_AGGREGATE_LIFT_API,
  GEO_LIFT_MARKET_SELECTION,
  GEO_LIFT_POWER_PLOT_API,
  STUDY_AGGREGATE_LIFT_API,
} from "../../../../constants/API";

/* ─────────────────────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────────────────────── */

interface StatTileProps {
  label: string;
  value: string;
  sub?: string;
  color: "violet" | "emerald" | "amber" | "sky";
  icon: React.ReactNode;
  loading?: boolean;
}

interface AggregateData {
  lift?: {
    totalLiftPercentage?: number;
    totalSalesWithCampaign?: number;
    totalSalesWithoutCampaign?: number;
  };
  roas?: {
    totalROAS?: number;
    totalAdSpendForSyntheticMarkets?: number;
    totalIncrementSales?: number;
  };
  graph?: {
    salesWithCampaignData?: number[];
    salesWithoutCampaignData?: number[];
    totalLiftPercentage?: number;
  };
}

interface LiftPlotData {
  lift_plot?: {
    c_obs?: Record<string, number>;
    t_obs?: Record<string, number>;
    Time?: Record<string, number>;
    date?: Record<string, string>;
    startCampaignDate?: Record<string, string>;
  };
  power_plot?: {
    EffectSize?: number[];
    Power?: number[];
    Investment?: number[];
  };
  pvalue?: number;
  lift_percentage?: number;
  salesWithCampaign?: number;
  salesWithoutCampaign?: number;
}

interface InsightsData {
  summary?: string;
  recommendations?: Array<{ type: string; message: string }>;
}

interface StudyArtifactsProps {
  jobConfigId: string | null;
  isComplete: boolean;
  studyName?: string;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────────────────── */

function fmt(n: number | undefined, decimals = 1): string {
  if (n === undefined || n === null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(decimals);
}

const colorMap = {
  violet: { bg: "var(--violet-subtle)", border: "var(--violet)", text: "var(--violet)", value: "var(--text-primary)" },
  emerald: { bg: "var(--success-subtle)", border: "var(--success)", text: "var(--success)", value: "#F0F4F8" },
  amber: { bg: "var(--warning-subtle)", border: "var(--warning)", text: "var(--warning)", value: "var(--text-primary)" },
  sky: { bg: "var(--blue-subtle)", border: "var(--blue)", text: "var(--blue)", value: "var(--text-primary)" },
};

/* ─────────────────────────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────────────────────────── */

const StatTile: React.FC<StatTileProps> = ({ label, value, sub, color, icon, loading }) => {
  const c = colorMap[color];
  return (
    <div
      className="rounded-xl border p-4 flex flex-col gap-2"
      style={{ background: c.bg, borderColor: c.border }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: c.text }}>{label}</span>
        <span style={{ color: c.text }}>{icon}</span>
      </div>
      {loading ? (
        <div className="h-7 w-20 rounded animate-pulse" style={{ background: "var(--bg-active)" }} />
      ) : (
        <p className="text-2xl font-bold tabular-nums" style={{ color: c.value }}>{value}</p>
      )}
      {sub && <p className="text-xs opacity-70" style={{ color: c.text }}>{sub}</p>}
    </div>
  );
};

const SectionTitle: React.FC<{ title: string; icon?: React.ReactNode }> = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    {icon && <span style={{ color: "var(--text-tertiary)" }}>{icon}</span>}
    <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-tertiary)" }}>{title}</h3>
  </div>
);

const Skeleton: React.FC<{ h?: string }> = ({ h = "h-48" }) => (
  <div className={`${h} rounded-xl animate-pulse`} style={{ background: "var(--bg-hover)" }} />
);

/* ─────────────────────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────────────────────── */

const StudyArtifacts: React.FC<StudyArtifactsProps> = ({
  jobConfigId,
  isComplete,
  studyName,
}) => {
  const [aggregate, setAggregate] = useState<AggregateData | null>(null);
  const [liftPlot, setLiftPlot] = useState<LiftPlotData | null>(null);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  /* ── Load all result data once job completes ────────────────────────── */
  useEffect(() => {
    if (!isComplete || !jobConfigId || fetchedRef.current) return;
    fetchedRef.current = true;
    loadResults(jobConfigId);
  }, [isComplete, jobConfigId]);

  async function loadResults(jcId: string) {
    setLoading(true);
    setLoadError(null);
    try {
      // Parallel fetch: aggregate lift + lift plot
      const [aggRes, liftRes] = await Promise.allSettled([
        axiosInstance.post(
          GEO_LIFT_AGGREGATE_LIFT_API,
          { jobConfigId: jcId, confidenceThreshold: 0, totalAdSpend: 0 },
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        ),
        axiosInstance.post(GEO_LIFT_MARKET_SELECTION, {
          marketSelectionId: 0,
          marketName: "",
          jobConfigId: jcId,
        }),
      ]);

      if (aggRes.status === "fulfilled") setAggregate(aggRes.value.data);
      if (liftRes.status === "fulfilled") setLiftPlot(liftRes.value.data);

      // Load AI insights (may not be ready yet — silent fail)
      try {
        const qcRes = await axiosInstance.get(
          `/api/v1/synthetic-control/study-chat/insights?job_config_id=${jcId}`
        );
        if (qcRes.data?.summary) setInsights(qcRes.data);
      } catch { /* insights not ready — skip */ }

    } catch (err) {
      setLoadError("Could not load results. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }

  /* ── Lift chart ECharts option ──────────────────────────────────────── */
  function buildLiftChartOption() {
    if (!liftPlot?.lift_plot) return null;
    const lp = liftPlot.lift_plot;
    const times = Object.values(lp.Time ?? {});
    const cObs = Object.values(lp.c_obs ?? {});
    const tObs = Object.values(lp.t_obs ?? {});
    const dates = Object.values(lp.date ?? {});
    const labels = dates.length ? dates : times.map(String);

    // Find campaign start index
    const startDates = Object.values(lp.startCampaignDate ?? {});
    let campaignStartIdx = -1;
    if (startDates.length && dates.length) {
      campaignStartIdx = dates.indexOf(startDates[0]);
    }

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: "#1e1b4b",
        textStyle: { color: "#e0e7ff", fontSize: 12 },
        formatter: (params: Array<{ seriesName: string; value: number; name: string }>) =>
          `<div style="padding:4px 8px"><div style="font-size:11px;color:#a5b4fc;margin-bottom:4px">${params[0]?.name}</div>` +
          params.map(p => `<div>${p.seriesName}: <b>${fmt(p.value)}</b></div>`).join("") + "</div>",
      },
      legend: {
        top: 8,
        textStyle: { color: "#9ca3af", fontSize: 11 },
        icon: "roundRect",
        itemWidth: 12,
        itemHeight: 4,
      },
      grid: { top: 40, right: 20, bottom: 40, left: 55, containLabel: false },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: { color: "#9ca3af", fontSize: 10, rotate: labels.length > 20 ? 30 : 0 },
        axisLine: { lineStyle: { color: "#e5e7eb" } },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#9ca3af", fontSize: 10, formatter: (v: number) => fmt(v) },
        splitLine: { lineStyle: { color: "#f3f4f6", type: "dashed" } },
      },
      series: [
        {
          name: "Treatment",
          type: "line",
          data: tObs,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#7c3aed", width: 2.5 },
          areaStyle: { color: "rgba(124,58,237,0.08)" },
          z: 3,
        },
        {
          name: "Synthetic Control",
          type: "line",
          data: cObs,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#6b7280", width: 1.5, type: "dashed" },
          z: 2,
        },
        ...(campaignStartIdx >= 0 ? [{
          name: "Campaign Start",
          type: "line",
          markLine: {
            silent: true,
            data: [{ xAxis: labels[campaignStartIdx] }],
            lineStyle: { color: "#f59e0b", type: "dashed", width: 1.5 },
            label: { formatter: "Campaign Start", color: "#f59e0b", fontSize: 10, position: "insideEndTop" },
          },
        }] : []),
      ],
    };
  }

  /* ── Power curve ECharts option ─────────────────────────────────────── */
  function buildPowerChartOption() {
    const pp = liftPlot?.power_plot;
    if (!pp?.EffectSize?.length) return null;
    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: "#1e1b4b",
        textStyle: { color: "#e0e7ff", fontSize: 12 },
      },
      grid: { top: 20, right: 20, bottom: 40, left: 45 },
      xAxis: {
        type: "category",
        data: pp.EffectSize.map(e => `${(e * 100).toFixed(0)}%`),
        name: "Effect Size",
        nameTextStyle: { color: "#9ca3af", fontSize: 10 },
        axisLabel: { color: "#9ca3af", fontSize: 10 },
        axisLine: { lineStyle: { color: "#e5e7eb" } },
      },
      yAxis: {
        type: "value",
        min: 0, max: 100,
        name: "Power (%)",
        nameTextStyle: { color: "#9ca3af", fontSize: 10 },
        axisLabel: { color: "#9ca3af", fontSize: 10, formatter: (v: number) => `${v}%` },
        splitLine: { lineStyle: { color: "#f3f4f6", type: "dashed" } },
      },
      series: [{
        type: "line",
        data: pp.Power?.map(p => (p * 100).toFixed(1)),
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { color: "#10b981", width: 2 },
        areaStyle: { color: "rgba(16,185,129,0.1)" },
        markLine: {
          silent: true,
          data: [{ yAxis: 80 }],
          lineStyle: { color: "#f59e0b", type: "dashed", width: 1.5 },
          label: { formatter: "80% power", color: "#f59e0b", fontSize: 10 },
        },
      }],
    };
  }

  /* ── Stats ──────────────────────────────────────────────────────────── */
  const liftPct = aggregate?.lift?.totalLiftPercentage;
  const roas = aggregate?.roas?.totalROAS;
  const incremental = aggregate?.roas?.totalIncrementSales ?? aggregate?.lift
    ? (aggregate?.lift?.totalSalesWithCampaign ?? 0) - (aggregate?.lift?.totalSalesWithoutCampaign ?? 0)
    : undefined;
  const pvalue = liftPlot?.pvalue;

  /* ── Waiting state ──────────────────────────────────────────────────── */
  if (!isComplete && !jobConfigId) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-5 p-8 text-center" style={{ background: "var(--bg-surface)" }}>
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: "var(--bg-hover)" }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
        </div>
        <div>
          <p className="text-base font-semibold" style={{ color: "var(--text-secondary)" }}>
            Results will appear here
          </p>
          <p className="text-sm mt-1 max-w-xs" style={{ color: "var(--text-tertiary)" }}>
            Complete the setup in the chat on the left — once your study runs,
            charts, tables, and AI insights will populate this panel.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 w-full max-w-xs mt-2">
          {["Lift %", "iROAS", "Incremental", "Confidence"].map(label => (
            <div key={label} className="h-20 rounded-xl border border-dashed flex items-center justify-center" style={{ background: "var(--bg-hover)", borderColor: "var(--border-default)" }}>
              <span className="text-xs" style={{ color: "var(--text-disabled)" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const liftOption = buildLiftChartOption();
  const powerOption = buildPowerChartOption();

  return (
    <div className="h-full overflow-y-auto px-5 py-5 space-y-6" style={{ background: "var(--bg-surface)" }}>
      {/* ── Study header ──────────────────────────────────────────────── */}
      {studyName && (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>{studyName}</p>
            <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>Synthetic Control Results</p>
          </div>
          {isComplete && (
            <span
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border"
              style={{ color: "var(--success)", background: "var(--success-subtle)", borderColor: "var(--success)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--success)" }} />
              Complete
            </span>
          )}
        </div>
      )}

      {/* ── Stat Tiles ────────────────────────────────────────────────── */}
      <div>
        <SectionTitle
          title="Key Metrics"
          icon={
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 20V10M12 20V4M6 20v-6"/>
            </svg>
          }
        />
        <div className="grid grid-cols-2 gap-3">
          <StatTile
            label="Aggregate Lift"
            value={loading ? "—" : liftPct !== undefined ? `+${liftPct.toFixed(1)}%` : "—"}
            sub="vs synthetic control"
            color="violet"
            loading={loading}
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
            }
          />
          <StatTile
            label="iROAS"
            value={loading ? "—" : roas !== undefined ? `${roas.toFixed(2)}x` : "—"}
            sub="incremental return on ad spend"
            color="emerald"
            loading={loading}
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
            }
          />
          <StatTile
            label="Incremental Sales"
            value={loading ? "—" : incremental !== undefined ? `$${fmt(incremental)}` : "—"}
            sub="additional revenue driven"
            color="amber"
            loading={loading}
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            }
          />
          <StatTile
            label="P-Value"
            value={loading ? "—" : pvalue !== undefined ? pvalue.toFixed(3) : "—"}
            sub={pvalue !== undefined && pvalue < 0.05 ? "✓ statistically significant" : pvalue !== undefined ? "not significant" : ""}
            color="sky"
            loading={loading}
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            }
          />
        </div>
      </div>

      {/* ── Lift Chart ────────────────────────────────────────────────── */}
      <div>
        <SectionTitle
          title="Treatment vs Synthetic Control"
          icon={
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          }
        />
        {loading ? (
          <Skeleton h="h-52" />
        ) : liftOption ? (
          <div className="rounded-xl border p-3 shadow-sm" style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)" }}>
            <ReactECharts option={liftOption} style={{ height: 200 }} />
          </div>
        ) : (
          <div className="h-52 rounded-xl flex items-center justify-center" style={{ background: "var(--bg-hover)" }}>
            <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>Lift plot not available</p>
          </div>
        )}
      </div>

      {/* ── Power Curve ───────────────────────────────────────────────── */}
      {(loading || powerOption) && (
        <div>
          <SectionTitle
            title="Power Curve"
            icon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 20V10M12 20V4M6 20v-6"/>
              </svg>
            }
          />
          {loading ? (
            <Skeleton h="h-40" />
          ) : powerOption ? (
            <div className="rounded-xl border p-3 shadow-sm" style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)" }}>
              <ReactECharts option={powerOption} style={{ height: 160 }} />
            </div>
          ) : null}
        </div>
      )}

      {/* ── Summary stats table ───────────────────────────────────────── */}
      {!loading && aggregate?.lift && (
        <div>
          <SectionTitle
            title="Summary"
            icon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
            }
          />
          <div className="rounded-xl border overflow-hidden shadow-sm" style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)" }}>
            {[
              ["Sales with Campaign", `$${fmt(aggregate.lift.totalSalesWithCampaign)}`],
              ["Sales without Campaign (Synthetic)", `$${fmt(aggregate.lift.totalSalesWithoutCampaign)}`],
              ["Incremental Sales", `$${fmt((aggregate.lift.totalSalesWithCampaign ?? 0) - (aggregate.lift.totalSalesWithoutCampaign ?? 0))}`],
              ...(aggregate.roas ? [
                ["Total Ad Spend", `$${fmt(aggregate.roas.totalAdSpendForSyntheticMarkets)}`],
                ["iROAS", `${aggregate.roas.totalROAS?.toFixed(2)}x`],
              ] : []),
            ].map(([label, value], i, arr) => (
              <div
                key={label}
                className="flex items-center justify-between px-4 py-3"
                style={i < arr.length - 1 ? { borderBottom: "1px solid var(--border-subtle)" } : undefined}
              >
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{label}</span>
                <span className="text-xs font-semibold tabular-nums" style={{ color: "var(--text-primary)" }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI Insights ───────────────────────────────────────────────── */}
      {insights && (
        <div>
          <SectionTitle
            title="AI Insights"
            icon={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
              </svg>
            }
          />
          <div className="rounded-xl border p-4 shadow-sm space-y-4" style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)" }}>
            {insights.summary && (
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {insights.summary}
              </p>
            )}
            {insights.recommendations?.map((rec, i) => (
              <div key={i} className="pl-3" style={{ borderLeft: "2px solid var(--violet)" }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--violet)" }}>
                  {rec.type.replace(/_/g, " ")}
                </p>
                <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: "var(--text-secondary)" }}>
                  {rec.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading skeleton for insights */}
      {loading && (
        <div>
          <SectionTitle title="AI Insights" />
          <Skeleton h="h-32" />
        </div>
      )}

      {/* Error state */}
      {loadError && (
        <div className="rounded-xl border p-4 text-center" style={{ background: "var(--error-subtle)", borderColor: "var(--error)" }}>
          <p className="text-xs" style={{ color: "var(--error)" }}>{loadError}</p>
          <button
            onClick={() => jobConfigId && loadResults(jobConfigId)}
            className="mt-2 text-xs underline"
            style={{ color: "var(--error)" }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Bottom padding */}
      <div className="h-4" />
    </div>
  );
};

export default StudyArtifacts;
